import { defineComponent as _defineComponent } from 'vue'
import { type Offer, ObjectType } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { UsePromotion } from '@/helpers/composables/usePromotion';
import type { AdServerFtCreativeFragment } from '@/features/ad-server/graphql/fragments/AdServerFTCreative.fragment';

import { computed, inject, ref } from 'vue';

import PromotionRowCTA from '@/features/buybox/components/list/PromotionRowCTA.vue';
import PromotionRowOfferLabel from '@/features/buybox/components/list/PromotionRowOfferLabel.vue';
import PromotionRowOfferPresentation from '@/features/buybox/components/list/PromotionRowOfferPresentation.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useTitlePlayerModal } from '@/features/title-player/composables/useTitlePlayerModal';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useFreeTrial } from '@/features/buybox/composables/useFreeTrial';
import { OFFER_ENTITYID_KEY, getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { OfferLocation } from '@/helpers/offer-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { useAdServer } from '@/features/ad-server/composables/useAdServer';
// EXP_PROVIDER_BUYBOX_V2
import { useProviderBuyboxExpTags } from '@/components/experiments/ProviderBuyboxExp/composables/useProviderBuyboxExpTags';
// EXP_PROVIDER_BUYBOX_V2
import { useGridMoreInfoExp } from '@/components/experiments/GridBuyboxRedesignExps/GridMoreInfoExp';
// EXP_LINEAR_TV_2
import { useLinearTVExp } from '@/components/experiments/LinearTVExp';
// EXP_LINEAR_TV_2
import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { useImpressionPixel } from '@/features/impression-pixels/composables/useImpressionPixel';
import useUnderPerformingUrlsExp from '@/components/experiments/UnderPerformingUrlsExp';
// EXP_CLEARER_FREE_TRIAL
import { useClearerFreeTrialExp } from '@/components/experiments/ClearerFreeTrialExp';
// EXP_CLEARER_FREE_TRIAL
// EXP_ATV_QTV_TRAILER_MODAL
import { useAtvQtvTrailerModalExp } from '@/components/experiments/AtvQtvTrailerModalExp';
// EXP_ATV_QTV_TRAILER_MODAL
// EXP_TITLE_PAGE_CATEGORIZATION
import { useTitlePageCategorizationExp } from '@/components/experiments/TitlePageCategorizationExp';
// EXP_TITLE_PAGE_CATEGORIZATION


export default /*#__PURE__*/_defineComponent({
  __name: 'ListPromotionRow',
  props: {
    title: null,
    offers: { default: () => [] },
    additionalContexts: { default: () => [] },
    hardcodedFallbackOfferId: null,
    hardcodedCountry: null,
    hardcodedShortName: null,
    hideRemoveAds: { type: Boolean },
    swiperIndex: null,
    largeFreeTrial: { type: Boolean },
    adServerCreative: null
  },
  emits: ["clickOut", "impressionCallback"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, offers: Offer[], additionalContexts: SnowplowContext[], hardcodedFallbackOfferId?: number, hardcodedCountry?: string | null, hardcodedShortName?: string, hideRemoveAds?: boolean, swiperIndex?: number, largeFreeTrial?: boolean, adServerCreative?: AdServerFtCreativeFragment };





const container = ref<HTMLElement | null>(null);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const buyboxLocation = inject('offerLocation', OfferLocation.REGULAR_BUYBOX);
const noOffer = inject<boolean | undefined>('noOffer', undefined);
const { placement } = getClickoutTrackingPlacement();

const { country, webLocale } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());
const { adServerIds } = useAdServer(() => props.title.id);

const { setNoOfferTag } = useInternationalOffersStatus({ title: () => props.title });
const { setGeoSwiperTags } = useGeoSwiper({ title: () => props.title });

const { isFallback, promotionOffer, packagePlan } = useFreeTrial({
	title: () => props.title,
	offers: () => props.offers,
	hardcodedCountry: () => props.hardcodedCountry,
	hardcodedShortName: () => props.hardcodedShortName,
	adServerCreative: () => props.adServerCreative,
});

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);
const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage({ entityId: jwRawEntityId });

const rowNumber = computed(() => {
	if (container.value == null) return undefined;

	const parentBuyBox = container.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(container.value));

	return index === -1 ? undefined : index;
});

// OVER_PROVIDER
// Bit of an odd case for the Promotion Placement to be a regular offer - I believe BI would like this for JWTV as well, so I think we will develop in this direction
// In this case over provider is halfway between a regular offer and a promotion offer, but they want to start tracking it as a regular offer
const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });
const clickoutType = computed(() => (isOverProviderTitle.value ? 'regular' : 'free_trial'));
const offerLocation = computed(() => (isOverProviderTitle.value ? buyboxLocation : OfferLocation.FREE_TRIAL));
// OVER_PROVIDER

const isGeoSwiperOffer = computed(() => props.hardcodedCountry != null && props.hardcodedCountry !== country.value);

/** Applies `uct_country`, `rowNumber`, `offerLocation`. */
function clickoutBase(u: ClickoutUrl) {
	return u
		.set('uct_country', country.value)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext.value)
		.setFreeTrial(!isOverProviderTitle.value)
		.setTitleSourceId(titleSourceId)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.setForTracking('offerLocation', offerLocation.value)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType.value)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.updateContext('ClickoutContextGraphql', 'swiperIndex', props.swiperIndex)
		.setPageType(props.title.objectType)
		.setCanary(xServeTrack.value)
		.setAdIds(props.adServerCreative ? adServerIds.value : null)
		.setGenre(props.title.content.genres);
}

const { setGridMoreInfoTags } = useGridMoreInfoExp();
// EXP_LINEAR_TV_2
const { setLinearTVExpTags } = useLinearTVExp();
// EXP_LINEAR_TV_2
// EXP_CLEARER_FREE_TRIAL
const { variant: cftrVariant, setClearerFreeTrialTags } = useClearerFreeTrialExp(
	() => promotionOffer.value?.package.shortName,
	isFallback
);
// variant_1 and variant_4 render the gold border also used for the highlighted (SHOW fallback) state.
const hasCftrBorder = computed(() => cftrVariant.value === 'variant_1' || cftrVariant.value === 'variant_4');
// EXP_CLEARER_FREE_TRIAL

// EXP_TITLE_PAGE_CATEGORIZATION
const { variant: titlePageCategorizationVariant, setTitlePageCategorizationExpTags } = useTitlePageCategorizationExp();
// EXP_TITLE_PAGE_CATEGORIZATION

// EXP_PROVIDER_BUYBOX_V2
const { setProviderBuyboxExpTag } = useProviderBuyboxExpTags();
// EXP_PROVIDER_BUYBOX_V2

const { setUnderPerformingUrlsExpTags } = useUnderPerformingUrlsExp();

// EXP_ATV_QTV_TRAILER_MODAL
const { setAtvQtvTrailerModalExpTags } = useAtvQtvTrailerModalExp();
// EXP_ATV_QTV_TRAILER_MODAL

function experimentAffiliation(u: ClickoutUrl) {
	return u
		.apply(u => setUnderPerformingUrlsExpTags(u, 'ft', promotionOffer.value!.package.shortName))
		.apply(u => setAtvQtvTrailerModalExpTags(u, 'ft', promotionOffer.value!.package.shortName));
}

const clickoutLink = computed(() => {
	if (!promotionOffer.value) return null;

	if (isArbitrating.value && ['amp', 'atp'].includes(promotionOffer.value.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({
			offer: promotionOffer,
			title: () => props.title,
			placement: 'ft',
		});
		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase);
		}
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer.value).apply(clickoutBase);

	// Geo Swiper
	if (isGeoSwiperOffer.value) {
		return clickoutUrl.apply(u =>
			setGeoSwiperTags(u, promotionOffer.value?.package.shortName, props.hardcodedCountry!)
		);
	}
	// Geo Swiper

	// International Offers
	clickoutUrl.apply(u => setNoOfferTag(u, promotionOffer.value?.package.shortName));
	// International Offers

	// EXP_PROVIDER_BUYBOX_V2
	clickoutUrl.apply(url =>
		setProviderBuyboxExpTag(
			url,
			'ft',
			promotionOffer.value?.package.shortName,
			promotionOffer.value?.monetizationType
		)
	);
	// EXP_PROVIDER_BUYBOX_V2

	clickoutUrl.apply(u => setGridMoreInfoTags(u, 'ft', promotionOffer.value?.package.shortName ?? ''));

	// EXP_LINEAR_TV_2
	clickoutUrl.apply(u =>
		setLinearTVExpTags({
			url: u,
			type: 'ft',
			shortName: promotionOffer.value?.package.shortName ?? '',
			technicalName: promotionOffer.value?.package.technicalName,
		})
	);
	// EXP_LINEAR_TV_2

	// EXP_CLEARER_FREE_TRIAL
	clickoutUrl.apply(u => setClearerFreeTrialTags(u, 'ft'));
	// EXP_CLEARER_FREE_TRIAL

	// EXP_TITLE_PAGE_CATEGORIZATION
	clickoutUrl.apply(u =>
		setTitlePageCategorizationExpTags({
			url: u,
			placement: 'ft',
			shortName: promotionOffer.value?.package.shortName ?? '',
		})
	);
	// EXP_TITLE_PAGE_CATEGORIZATION

	// Experiments
	clickoutUrl.apply(experimentAffiliation);
	// Experiments

	return clickoutUrl;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
	groupId: 'listPromotionRow',
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;
	if (clickoutLink.value) onIntersection(clickoutLink.value, isVisible);
}

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const hasPromotedTag = computed(() => isFallback.value && !props.largeFreeTrial);

/* PIXEL IMPRESSION */
useImpressionPixel(container, webLocale, {
	shortNames: () => promotionOffer.value?.package.shortName,
});

// EXP_TITLE_PAGE_CATEGORIZATION
// variant_4 shows the "more info" checklist, regardless of whether this row is competing with
// other promo items in a swiper (the only case `largeFreeTrial` is otherwise set for).
const isTitlePageCategorizationDetailsVariant = computed(() => titlePageCategorizationVariant.value === 'variant_4');
const showMoreInfoDetails = computed(() => props.largeFreeTrial || isTitlePageCategorizationDetailsVariant.value);
// EXP_TITLE_PAGE_CATEGORIZATION

// CTA has a border, and different text. Fallback only.
const isHighlighted = computed(
	() =>
		isFallback.value &&
		(['SHOW', 'SHOW_SEASON'].includes(props.title.objectType) ||
			// EXP_TITLE_PAGE_CATEGORIZATION: variant_3 extends the Show/Season highlighted (gold border +
			// highlighted "similar titles" text) treatment to Movie.
			(props.title.objectType === ObjectType.Movie && titlePageCategorizationVariant.value === 'variant_3'))
);

const promotionShortName = computed(() => promotionOffer.value?.package.shortName ?? '');
const { isModalProvider, openPlayerModal } = useTitlePlayerModal(promotionShortName);

const { t, tc } = useI18n();
const freeTrialDeviceCount = computed(() => (promotionOffer.value?.package.shortName === 'atp' ? 6 : 3));
const freeTrialDetails = computed(() => [
	t('WEBAPP_PP_FT_DETAILS_1_COUNT', { count: freeTrialDeviceCount.value }),
	tc('WEBAPP_PP_FT_EXP_DETAILS_2'),
	tc('WEBAPP_PP_FT_EXP_DETAILS_3'),
]);

async function openModal() {
	if (!isModalProvider.value || !clickoutLink.value || !promotionOffer.value) return;
	await openPlayerModal({ offer: promotionOffer.value, title: props.title, clickoutUrl: clickoutLink.value });
	onClickOut();
}

function onClickOut() {
	trackTagEvent(promotionOffer);

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
}

return { __sfc: true,props, emit, container, titleContext, filterOption, titleSourceId, episodeEntityId, buyboxLocation, noOffer, placement, country, webLocale, xServeTrack, adServerIds, setNoOfferTag, setGeoSwiperTags, isFallback, promotionOffer, packagePlan, jwRawEntityId, trackTagEvent, withArbitrageTracking, isArbitrating, rowNumber, isOverProviderTitle, clickoutType, offerLocation, isGeoSwiperOffer, clickoutBase, setGridMoreInfoTags, setLinearTVExpTags, cftrVariant, setClearerFreeTrialTags, hasCftrBorder, titlePageCategorizationVariant, setTitlePageCategorizationExpTags, setProviderBuyboxExpTag, setUnderPerformingUrlsExpTags, setAtvQtvTrailerModalExpTags, experimentAffiliation, clickoutLink, onIntersection, onVisibilityChanged, observeVisibilityOptions, hasPromotedTag, isTitlePageCategorizationDetailsVariant, showMoreInfoDetails, isHighlighted, promotionShortName, isModalProvider, openPlayerModal, t, tc, freeTrialDeviceCount, freeTrialDetails, openModal, onClickOut, PromotionRowCTA, PromotionRowOfferLabel, PromotionRowOfferPresentation, ClickoutAnchor, ProviderIcon, faCheck }
}

})